import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import DateRangePicker from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DomainBehaviourDay } from '@/domain/cages/overview.types';
import {
    findLastSwimSpeedDay,
    OverviewChartCard,
    useOverviewChartSpecs,
} from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { localizedDateFormat } from '@/utils/date';

import styles from './CageWelfareFishSpeedView.module.scss';

const CHART_HEIGHT = 400;
const INFO_COLUMN_WIDTH = 375;
const SKELETON_WIDTH = 100;
const SKELETON_HEIGHT = 24;
const SKELETON_RADIUS = 8;

export interface CageWelfareFishSpeedViewProps {
    days: DomainBehaviourDay[];
    /** Dayparts that get a series. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    /** The single daypart the charts that do not fan out should plot. */
    daypartTag: string;
    weightUnitTag: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (dates: DateRange) => void;
}

export default function CageWelfareFishSpeedView(props: CageWelfareFishSpeedViewProps) {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
    } = props;
    const { t } = useTranslation('cageCharts');
    // Page copy, not chart copy.
    const { t: tRoot, i18n } = useTranslation();

    const specs = useOverviewChartSpecs({
        days: [],
        behaviourDays: days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
    });

    const lastSwimSpeedDay = findLastSwimSpeedDay(days);

    return (
        <>
            <div className={styles.datePickerRow}>
                <DateRangePicker
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId="none"
                    menuPosition="left"
                    disabled={isLoading}
                />
            </div>
            <GridLayout contentLayout="1-fixed" secondColumnWidth={INFO_COLUMN_WIDTH}>
                <OverviewChartCard
                    title={t('title.swimSpeed')}
                    chartName="cage-swim-speed"
                    spec={specs.swimSpeed}
                    chartHeight={CHART_HEIGHT}
                    isLoading={isLoading}
                    error={error}
                    {...retry}
                    showDateRangePicker={false}
                />
                <ContentBlock
                    title={tRoot('common.labels.information')}
                    titleIcon={<TitleIcon icon={Info} />}>
                    <div className={styles.infoText}>
                        {tRoot('cageWelfare.fishspeed.description')}
                    </div>
                    <div className={styles.measurementRow}>
                        <Caption>{tRoot('common.labels.lastMeasurementDay')}</Caption>
                        {isLoading ? (
                            <SkeletonBox
                                width={SKELETON_WIDTH}
                                height={SKELETON_HEIGHT}
                                borderRadius={SKELETON_RADIUS}
                            />
                        ) : (
                            <span className={styles.measurementValue}>
                                {lastSwimSpeedDay
                                    ? localizedDateFormat({
                                          dateString: lastSwimSpeedDay.day,
                                          onlydate: true,
                                          locale: i18n.language,
                                      })
                                    : tRoot('common.labels.noData')}
                            </span>
                        )}
                    </div>
                </ContentBlock>
            </GridLayout>
        </>
    );
}
